import React from 'react';

import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

import { alpha } from '@mui/material/styles';

import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';

import { MONO_FONT, TOKENS } from '../../../theme/muiTheme';

import { formatTonne } from '../../../shared/utils/number';

const CARBON_LEDGER_MONO = `'IBM Plex Mono', ${MONO_FONT}`;

const STRIP_TEAL = TOKENS.primary;

const numericValue = value => (
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? 0
    : Number(value)
);

/** Shape one accounting group into its verified and pending totals. */

export const secondaryImpactPresentation = (metric = {}) => ({
  unverifiedTco2e: numericValue(metric.unverifiedTco2e ?? metric.unverified_tco2e),
  verifiedTco2e: numericValue(metric.verifiedTco2e ?? metric.verified_tco2e),
});

/** Decide whether the strip has anything worth showing. */

export const secondaryStripVisible = (carbonCredit = {}, otherClimateImpact = {}) => {
  const credit = secondaryImpactPresentation(carbonCredit);
  const other = secondaryImpactPresentation(otherClimateImpact);
  return credit.verifiedTco2e > 0
    || credit.unverifiedTco2e > 0
    || other.verifiedTco2e > 0
    || other.unverifiedTco2e > 0;
};

function StripRow({ label, metric, t }) {

  const presentation = secondaryImpactPresentation(metric);

  return (

    <Stack direction={'row'} alignItems={'baseline'} flexWrap={'wrap'} spacing={1}>

      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{label}</Typography>

      <Typography sx={{

        color: STRIP_TEAL, fontFamily: CARBON_LEDGER_MONO, fontSize: 17, fontWeight: 700,

        letterSpacing: '-0.03em', whiteSpace: 'nowrap',

      }}>

        {formatTonne(presentation.verifiedTco2e, { withUnit: true })}

      </Typography>

      {presentation.unverifiedTco2e > 0 && (

        <Typography color={'text.secondary'} sx={{ fontSize: 12 }}>

          {t('climateAction.secondaryImpact.unverified', {

            defaultValue: 'Unverified: {{value}}',

            value: formatTonne(presentation.unverifiedTco2e, { withUnit: true }),

          })}

        </Typography>

      )}

    </Stack>

  );

}

/** Compact secondary strip replacing the retired-credit equation block (P5). */

export default function SecondaryImpactStrip({ carbonCredit = {}, otherClimateImpact = {}, t }) {

  if (!secondaryStripVisible(carbonCredit, otherClimateImpact)) return null;

  return (

    <Card component={'section'} data-testid={'secondary-impact-strip'} sx={{

      bgcolor: 'background.paper',

      borderColor: 'divider',

      borderRadius: 1.75,

      boxShadow: theme => (theme.palette.mode === 'dark'

        ? 'none'

        : '0 10px 30px rgba(24, 39, 51, 0.045)'),

      overflow: 'hidden',

    }}>

      <CardContent sx={{ p: { xs: 2, md: 2.25 }, '&:last-child': { pb: { xs: 2, md: 2.25 } } }}>

        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent={'space-between'} spacing={1.5}>

          <Stack direction={'row'} alignItems={'center'} spacing={1.25}>

            <Box sx={{

              alignItems: 'center', bgcolor: alpha(STRIP_TEAL, 0.1), borderRadius: 2,

              color: STRIP_TEAL, display: 'flex', height: 38, justifyContent: 'center', width: 38,

            }}>

              <DonutLargeOutlinedIcon sx={{ fontSize: 22 }} />

            </Box>

            <Box>

              <Typography component={'h2'} sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25 }}>

                {t('climateAction.secondaryImpact.title', { defaultValue: 'Tracked climate impact' })}

              </Typography>

            </Box>

          </Stack>

        </Stack>

        <Stack spacing={1} sx={{ mt: 1.5 }}>

          <StripRow

            label={t('climateAction.secondaryImpact.retiredCredits', { defaultValue: 'Retired carbon credits' })}

            metric={carbonCredit}

            t={t}

          />

          <StripRow

            label={t('climateAction.secondaryImpact.otherImpact', { defaultValue: 'Other climate impact' })}

            metric={otherClimateImpact}

            t={t}

          />

        </Stack>

      </CardContent>

    </Card>

  );

}